import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, ClipboardCopy, Loader2, RefreshCw, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/resultExport';
import { STALE_CHUNK_ERROR, reloadForNewBuild } from '@/lib/staleBuild';

type Scope = 'page' | 'app';
interface Props {
  scope: Scope;
  children: ReactNode;
  /** A change clears the error (the page boundary passes the path, so navigating away recovers). */
  resetKey?: string;
}
interface State { error: Error | null; componentStack: string; copied: boolean; reloading: boolean }

const isStaleBuild = (error: unknown) => STALE_CHUNK_ERROR.test(String((error as Error)?.message ?? error));

/** Everything we'd want in a bug report, as plain text. */
const details = (error: Error, componentStack: string) => [
  `${error.name}: ${error.message}`,
  '',
  `Page: ${window.location.href}`,
  `When: ${new Date().toISOString()}`,
  `Browser: ${navigator.userAgent}`,
  '',
  error.stack ?? '(no stack)',
  '',
  `Components:${componentStack || ' (none)'}`,
].join('\n');

/**
 * Catches an error thrown while rendering, so the app shows what happened instead of going blank.
 * Without one, React unmounts the whole tree on any render error and leaves an empty page
 * (app.gizmodata.com, 2026-10-15: /cluster/:id went blank now and then in Safari). The page-level
 * boundary sits inside the layout, so the sidebar and header keep working and navigating away
 * clears it; the app-level one is the last resort around everything. A page file from a replaced
 * build (a deploy while the tab was open) isn't shown as an error: it reloads onto the new build, like
 * lazyPage, and says a new version is available.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: '', copied: false, reloading: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error instanceof Error ? error : new Error(String(error)), copied: false };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const componentStack = info.componentStack ?? '';
    this.setState({ componentStack });
    console.error(`[${this.props.scope} error boundary]`, error, componentStack);
    // A file from a build a deploy replaced: reload onto the new one (a few tries, see staleBuild).
    if (isStaleBuild(error)) this.setState({ reloading: reloadForNewBuild() });
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: '', copied: false, reloading: false });
    }
  }

  private copy = () => {
    const { error, componentStack } = this.state;
    if (!error) return;
    copyText(details(error, componentStack))
      .then(() => this.setState({ copied: true }))
      .catch(() => { /* the message is on screen to select by hand */ });
  };

  render() {
    const { error, copied, reloading } = this.state;
    if (!error) return this.props.children;
    const page = this.props.scope === 'page';
    if (isStaleBuild(error)) {
      // Not a fault: a new version was released while this tab was open.
      return (
        <div role="alert" data-testid="error-boundary-update"
             className={page ? 'py-10' : 'min-h-screen flex items-center justify-center bg-background p-6'}>
          <div className="mx-auto max-w-xl rounded-lg border bg-card p-6 space-y-4 text-card-foreground">
            <div className="flex items-start gap-3">
              {reloading ? <Loader2 className="h-6 w-6 shrink-0 animate-spin text-primary" />
                : <Sparkles className="h-6 w-6 shrink-0 text-primary" />}
              <div className="space-y-1">
                <h1 className="text-lg font-semibold">{reloading ? 'Loading the new version…' : 'A new version is available'}</h1>
                <p className="text-sm text-muted-foreground">
                  The app was updated while this page was open. {reloading ? 'This takes a moment.' : 'Reload to get the new version.'}
                </p>
              </div>
            </div>
            {!reloading && (
              <Button onClick={() => window.location.reload()}>
                <RefreshCw className="h-4 w-4 mr-2" /> Reload
              </Button>
            )}
          </div>
        </div>
      );
    }
    return (
      <div role="alert" data-testid="error-boundary"
           className={page ? 'py-10' : 'min-h-screen flex items-center justify-center bg-background p-6'}>
        <div className="mx-auto max-w-2xl rounded-lg border bg-card p-6 space-y-4 text-card-foreground">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 shrink-0 text-destructive" />
            <div className="space-y-1">
              <h1 className="text-lg font-semibold">
                {page ? 'This page hit an error' : 'Something went wrong'}
              </h1>
              <p className="text-sm text-muted-foreground">
                It stopped instead of showing something wrong. Reloading usually fixes it. If it keeps happening,
                copy the details and send them to support.
              </p>
            </div>
          </div>
          <pre className="max-h-40 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-words"
               data-testid="error-boundary-message">{`${error.name}: ${error.message}`}</pre>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" /> Reload
            </Button>
            {page && (
              <Button variant="outline" onClick={() => this.setState({ error: null, componentStack: '', copied: false, reloading: false })}>
                <RotateCcw className="h-4 w-4 mr-2" /> Try again
              </Button>
            )}
            <Button variant="outline" onClick={this.copy}>
              <ClipboardCopy className="h-4 w-4 mr-2" /> {copied ? 'Copied' : 'Copy details'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/** The page-level boundary: cleared whenever the route changes. */
export const PageErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  return <AppErrorBoundary scope="page" resetKey={pathname}>{children}</AppErrorBoundary>;
};
